import React, { useState, useEffect } from 'react';

const ContractFormPage = ({ contractId, onSave, onCancel }) => {
  const [contract, setContract] = useState({
    clientId: '',
    templateId: '',
    number: '',
    startDate: new Date().toISOString().split('T')[0],
    dueDate: `${new Date().getFullYear()}-12-31`,
    isLegacy: false,
    customContent: '',
    services: []
  });

  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isEditing = Boolean(contractId);

  useEffect(() => {
    fetchInitialData();
    if (isEditing) {
      fetchContract();
    }
  }, [contractId]);

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const [clientsRes, templatesRes, servicesRes] = await Promise.all([
        fetch('/api/clients', { headers }),
        fetch('/api/templates/active', { headers }),
        fetch('/api/services', { headers })
      ]);

      if (clientsRes.ok) {
        const clientsData = await clientsRes.json();
        setClients(clientsData);
      }

      if (templatesRes.ok) {
        const templatesData = await templatesRes.json();
        setTemplates(templatesData);
      }

      if (servicesRes.ok) {
        const servicesData = await servicesRes.json();
        setAvailableServices(servicesData);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchContract = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/contracts/${contractId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setContract({
          clientId: data.clientId,
          templateId: data.templateId || '',
          number: data.number,
          startDate: data.startDate.split('T')[0],
          dueDate: data.dueDate.split('T')[0],
          isLegacy: data.isLegacy,
          customContent: data.customContent || '',
          services: data.services || []
        });
        setSelectedServices(data.services || []);
      } else {
        throw new Error('Eroare la încărcarea contractului');
      }
    } catch (error) {
      console.error('Error fetching contract:', error);
      alert('Eroare la încărcarea datelor contractului');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContract(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleServiceAdd = (serviceId) => {
    const service = availableServices.find(s => s.id === parseInt(serviceId));
    if (service && !selectedServices.find(s => s.serviceId === service.id)) {
      const newService = {
        serviceId: service.id,
        serviceName: service.name,
        unit: service.unit,
        price: service.price,
        currency: service.currency,
        quantity: 1,
        customPrice: null
      };
      setSelectedServices([...selectedServices, newService]);
    }
  };

  const handleServiceUpdate = (serviceId, field, value) => {
    setSelectedServices(prev => 
      prev.map(service => 
        service.serviceId === serviceId 
          ? { ...service, [field]: field === 'quantity' ? parseInt(value) || 1 : parseFloat(value) || null }
          : service
      )
    );
  };

  const handleServiceRemove = (serviceId) => {
    setSelectedServices(prev => prev.filter(service => service.serviceId !== serviceId));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!contract.clientId) {
      newErrors.clientId = 'Clientul este obligatoriu';
    }

    if (!contract.number.trim()) {
      newErrors.number = 'Numărul contractului este obligatoriu';
    }

    if (!contract.startDate) {
      newErrors.startDate = 'Data de început este obligatorie';
    }

    if (!contract.dueDate) {
      newErrors.dueDate = 'Data de expirare este obligatorie';
    } else if (new Date(contract.dueDate) <= new Date(contract.startDate)) {
      newErrors.dueDate = 'Data de expirare trebuie să fie după data de început';
    }

    if (selectedServices.length === 0) {
      newErrors.services = 'Cel puțin un serviciu trebuie selectat';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = isEditing ? `/api/contracts/${contractId}` : '/api/contracts';
      const method = isEditing ? 'PUT' : 'POST';

      const contractData = {
        ...contract,
        services: selectedServices.map(service => ({
          serviceId: service.serviceId,
          quantity: service.quantity,
          customPrice: service.customPrice
        }))
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contractData),
      });

      if (response.ok) {
        const savedContract = await response.json();
        alert(isEditing ? 'Contract actualizat cu succes!' : 'Contract creat cu succes!');
        if (onSave) {
          onSave(savedContract);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Eroare la salvarea contractului');
      }
    } catch (error) {
      console.error('Error saving contract:', error);
      alert('Eroare la salvarea contractului: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      window.history.back();
    }
  };

  const calculateTotal = () => {
    return selectedServices.reduce((total, service) => {
      const price = service.customPrice || service.price;
      return total + (price * service.quantity);
    }, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Editare Contract' : 'Contract Nou'}
              </h1>
              <p className="text-gray-600 mt-1">
                {isEditing ? 'Modificați informațiile contractului' : 'Completați informațiile pentru contractul nou'}
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Informații de bază */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informații de Bază</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client *
                </label>
                <select
                  name="clientId"
                  value={contract.clientId}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.clientId ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Selectează client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
                {errors.clientId && <p className="text-red-500 text-sm mt-1">{errors.clientId}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template
                </label>
                <select
                  name="templateId"
                  value={contract.templateId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Fără template</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Număr Contract *
                </label>
                <input
                  type="text"
                  name="number"
                  value={contract.number}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.number ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="CNT-2024-0001"
                />
                {errors.number && <p className="text-red-500 text-sm mt-1">{errors.number}</p>}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isLegacy"
                  checked={contract.isLegacy}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Contract Legacy
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Început *
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={contract.startDate}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.startDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Expirare *
                </label>
                <input
                  type="date"
                  name="dueDate"
                  value={contract.dueDate}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.dueDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.dueDate && <p className="text-red-500 text-sm mt-1">{errors.dueDate}</p>}
              </div>
            </div>
          </div>

          {/* Servicii */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Servicii</h2>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleServiceAdd(e.target.value);
                    e.target.value = '';
                  }
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Adaugă serviciu</option>
                {availableServices
                  .filter(service => !selectedServices.find(s => s.serviceId === service.id))
                  .map(service => (
                    <option key={service.id} value={service.id}>
                      {service.name} - {service.price} {service.currency}
                    </option>
                  ))
                }
              </select>
            </div>

            {errors.services && <p className="text-red-500 text-sm mb-4">{errors.services}</p>}

            {selectedServices.length > 0 ? (
              <div className="space-y-4">
                {selectedServices.map((service, index) => (
                  <div key={service.serviceId} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-medium text-gray-900">{service.serviceName}</h3>
                        <p className="text-sm text-gray-500">{service.unit}</p>
                      </div>
                      <button
                        onClick={() => handleServiceRemove(service.serviceId)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cantitate
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={service.quantity}
                          onChange={(e) => handleServiceUpdate(service.serviceId, 'quantity', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Preț Personalizat
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={`${service.price} ${service.currency}`}
                          value={service.customPrice || ''}
                          onChange={(e) => handleServiceUpdate(service.serviceId, 'customPrice', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Total
                        </label>
                        <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                          {((service.customPrice || service.price) * service.quantity).toFixed(2)} {service.currency}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center text-lg font-semibold">
                    <span>Total Contract:</span>
                    <span className="text-blue-600">{calculateTotal().toFixed(2)} RON</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Nu sunt servicii adăugate. Selectați servicii din dropdown-ul de mai sus.
              </div>
            )}
          </div>

          {/* Conținut personalizat */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Conținut Personalizat</h2>
            <textarea
              name="customContent"
              value={contract.customContent}
              onChange={handleInputChange}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Clauze contractuale personalizate..."
            />
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                {saving ? 'Se salvează...' : (isEditing ? 'Actualizează' : 'Creează')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractFormPage;